import React from 'react';
import { WxbIcon } from '../Icon/Icon';
import './Pagination.css';

export interface WxbPaginationProps extends Omit<React.HTMLAttributes<HTMLElement>, 'onChange'> {
  current: number;
  total: number;
  pageSize?: number;
  onChange?: (page: number) => void;
}

type PaginationItem = number | 'ellipsis';

export const WxbPagination: React.FC<WxbPaginationProps> = ({
  current,
  total,
  pageSize = 10,
  onChange,
  className = '',
  'aria-label': ariaLabel = '分页导航',
  ...props
}) => {
  const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 1;
  const safeTotal = Number.isFinite(total) ? Math.max(0, total) : 0;
  const requestedPage = Number.isFinite(current) ? Math.trunc(current) : 1;
  const pages = Math.max(1, Math.ceil(safeTotal / safePageSize));
  const activePage = Math.min(pages, Math.max(1, requestedPage));

  const getRange = () => {
    const delta = 2;
    const range: PaginationItem[] = [];
    for (let page = 1; page <= pages; page += 1) {
      if (page === 1 || page === pages || (page >= activePage - delta && page <= activePage + delta)) {
        range.push(page);
      } else if (range[range.length - 1] !== 'ellipsis') {
        range.push('ellipsis');
      }
    }
    return range;
  };

  return (
    <nav
      {...props}
      className={`wxb-pagination ${className}`}
      aria-label={ariaLabel}
    >
      <ol className="wxb-pagination-list">
        <li>
          <button
            type="button"
            className="wxb-page-btn"
            aria-label="上一页"
            disabled={activePage <= 1}
            onClick={() => onChange?.(activePage - 1)}
          >
            <WxbIcon name="chevron-left" size={16} aria-hidden="true" />
          </button>
        </li>
        {getRange().map((item, index) => (
          item === 'ellipsis' ? (
            <li key={`ellipsis-${index}`} className="wxb-page-dots" aria-hidden="true">…</li>
          ) : (
            <li key={item}>
              <button
                type="button"
                className={`wxb-page-btn ${item === activePage ? 'is-active' : ''}`}
                aria-label={`第 ${item} 页`}
                aria-current={item === activePage ? 'page' : undefined}
                onClick={() => onChange?.(item)}
              >
                {item}
              </button>
            </li>
          )
        ))}
        <li>
          <button
            type="button"
            className="wxb-page-btn"
            aria-label="下一页"
            disabled={activePage >= pages}
            onClick={() => onChange?.(activePage + 1)}
          >
            <WxbIcon name="chevron-right" size={16} aria-hidden="true" />
          </button>
        </li>
      </ol>
    </nav>
  );
};
